<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  selectedColumns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:visible', 'save']);

const { t } = useI18n();

// Định nghĩa tất cả các cột có thể hiển thị
const availableColumns = ref([
  {
    key: 'externalOrderId',
    label: 'TABLE.ORDER_ID',
    required: true,
    width: 'min-w-[120px]',
  },
  {
    key: 'shopName',
    label: 'TABLE.SHOP',
    required: false,
    width: 'min-w-[120px]',
  },
  {
    key: 'buyerName',
    label: 'TABLE.BUYER',
    required: true,
    width: 'min-w-[150px]',
  },
  {
    key: 'source',
    label: 'TABLE.SOURCE',
    required: false,
    width: 'min-w-[100px]',
  },
  {
    key: 'status',
    label: 'TABLE.STATUS',
    required: true,
    width: 'min-w-[110px]',
  },
  {
    key: 'totalCents',
    label: 'TABLE.TOTAL',
    required: true,
    width: 'min-w-[100px]',
  },
  {
    key: 'paidAt',
    label: 'TABLE.PAYMENT_STATUS',
    required: false,
    width: 'min-w-[130px]',
  },
  {
    key: 'placedAt',
    label: 'TABLE.PLACED_DATE',
    required: true,
    width: 'min-w-[110px]',
  },
  {
    key: 'isGift',
    label: 'TABLE.TAGS',
    required: false,
    width: 'min-w-[100px]',
  },
  {
    key: 'actions',
    label: 'TABLE.ACTIONS',
    required: true,
    width: 'min-w-[140px]',
  },
  {
    key: 'currencyCode',
    label: 'TABLE.CURRENCY',
    required: false,
    width: 'min-w-[80px]',
  },
  {
    key: 'shippingCents',
    label: 'TABLE.SHIPPING',
    required: false,
    width: 'min-w-[100px]',
  },
  {
    key: 'taxCents',
    label: 'TABLE.TAX',
    required: false,
    width: 'min-w-[80px]',
  },
  {
    key: 'discountCents',
    label: 'TABLE.DISCOUNT',
    required: false,
    width: 'min-w-[100px]',
  },
  {
    key: 'fulfilledAt',
    label: 'TABLE.FULFILLED_DATE',
    required: false,
    width: 'min-w-[130px]',
  },
  {
    key: 'expectedDeliveryDate',
    label: 'TABLE.EXPECTED_DELIVERY',
    required: false,
    width: 'min-w-[130px]',
  },
  {
    key: 'noteFromBuyer',
    label: 'TABLE.NOTE',
    required: false,
    width: 'min-w-[150px]',
  },
  {
    key: 'giftMessage',
    label: 'TABLE.GIFT_MESSAGE',
    required: false,
    width: 'min-w-[150px]',
  },
  {
    key: 'orderUrl',
    label: 'TABLE.ORDER_URL',
    required: false,
    width: 'min-w-[120px]',
  },
]);

const selectedColumns = ref([]);

// Khởi tạo selectedColumns từ props
watch(() => props.selectedColumns, (newVal) => {
  selectedColumns.value = [...newVal];
}, { immediate: true });

const toggleColumn = (columnKey) => {
  const column = availableColumns.value.find(col => col.key === columnKey);
  if (column.required) return; // Không cho phép tắt cột bắt buộc
  
  const index = selectedColumns.value.indexOf(columnKey);
  if (index > -1) {
    selectedColumns.value.splice(index, 1);
  } else {
    selectedColumns.value.push(columnKey);
  }
};

const isColumnSelected = (columnKey) => {
  return selectedColumns.value.includes(columnKey);
};

const isColumnRequired = (columnKey) => {
  const column = availableColumns.value.find(col => col.key === columnKey);
  return column?.required || false;
};

const handleSave = () => {
  emit('save', selectedColumns.value);
  emit('update:visible', false);
};

const handleCancel = () => {
  selectedColumns.value = [...props.selectedColumns];
  emit('update:visible', false);
};

const moveUp = (index) => {
  if (index > 0) {
    const item = selectedColumns.value.splice(index, 1)[0];
    selectedColumns.value.splice(index - 1, 0, item);
  }
};

const moveDown = (index) => {
  if (index < selectedColumns.value.length - 1) {
    const item = selectedColumns.value.splice(index, 1)[0];
    selectedColumns.value.splice(index + 1, 0, item);
  }
};
</script>

<template>
  <woot-modal
    :show="visible"
    size="large"
    @close="handleCancel"
  >
    <div class="p-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
        {{ t('COLUMN_CONFIG.TITLE') }}
      </h3>
      
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-6">
        {{ t('COLUMN_CONFIG.DESCRIPTION') }}
      </p>

      <!-- Danh sách cột đã chọn -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ t('COLUMN_CONFIG.SELECTED_COLUMNS') }}
        </h4>
        <div class="space-y-2">
          <div
            v-for="(columnKey, index) in selectedColumns"
            :key="columnKey"
            class="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
          >
            <div class="flex items-center">
              <fluent-icon
                icon="grip-vertical"
                size="16"
                class="text-slate-400 mr-2 cursor-move"
              />
              <span class="text-sm text-slate-900 dark:text-slate-100">
                {{ t(availableColumns.find(col => col.key === columnKey)?.label) }}
              </span>
              <span
                v-if="isColumnRequired(columnKey)"
                class="ml-2 text-xs text-hichat-500"
              >
                ({{ t('COLUMN_CONFIG.REQUIRED') }})
              </span>
            </div>
            <div class="flex items-center space-x-2">
              <woot-button
                size="tiny"
                icon="arrow-up"
                variant="clear"
                :disabled="index === 0"
                @click="moveUp(index)"
              />
              <woot-button
                size="tiny"
                icon="arrow-down"
                variant="clear"
                :disabled="index === selectedColumns.length - 1"
                @click="moveDown(index)"
              />
              <woot-button
                v-if="!isColumnRequired(columnKey)"
                size="tiny"
                icon="dismiss"
                variant="clear"
                @click="toggleColumn(columnKey)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Danh sách cột có thể thêm -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ t('COLUMN_CONFIG.AVAILABLE_COLUMNS') }}
        </h4>
        <div class="grid grid-cols-2 gap-2">
          <div
            v-for="column in availableColumns"
            :key="column.key"
            class="flex items-center p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
            :class="{
              'opacity-50 cursor-not-allowed': isColumnRequired(column.key),
              'bg-hichat-50 dark:bg-hichat-900 border-hichat-200 dark:border-hichat-700': isColumnSelected(column.key)
            }"
            @click="toggleColumn(column.key)"
          >
            <woot-button
              :icon="isColumnSelected(column.key) ? 'checkmark' : 'add'"
              size="tiny"
              variant="clear"
              :disabled="isColumnRequired(column.key)"
            />
            <span class="ml-2 text-sm text-slate-900 dark:text-slate-100">
              {{ t(column.label) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
        <woot-button
          variant="clear"
          @click="handleCancel"
        >
          {{ t('COLUMN_CONFIG.CANCEL') }}
        </woot-button>
        <woot-button
          @click="handleSave"
        >
          {{ t('COLUMN_CONFIG.SAVE') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
